/**
 * House of Dusk · fiches produit, coque commune des lots 5 et 6.
 * Contrat : 00_AI_BRAIN/06_RELEVE_THEME/lots/lot5-6_coque.md.
 *
 * <hod-product> : tient la variante choisie et l'offre en cours. Sans fetch de page (11 §2.10 et
 *   §2.11 : jamais le <variant-picker> d'Horizon) : l'ilot JSON [data-hod-pdp-data] porte les
 *   variantes, chaque zone qui suit la variante porte sa table data-hod-map (id de variante vers
 *   texte ou HTML rendu par Liquid). Met a jour l'URL ?variant= (replaceState), la galerie, le
 *   bouton d'ajout et la barre fixe (data-lines lues au clic par hod-cart.js, seul module qui
 *   ecrit le panier). API : selectVariant(id), setOffer(cle, offre|null), getState().
 *   Evenements (bubbles) : hod:pdp:ready, hod:pdp:variant, hod:pdp:offer.
 *   Apercu sans ecriture de donnees : ?hod_preview=<cle> revele [data-hod-preview-reveal~="<cle>"].
 * <hod-pdp-gallery> : vignettes, fleches en boucle, slider mobile et compteur « 01 / 06 ».
 * <hod-pdp-sticky> : barre d'achat fixe. SPEC V2 §3.2 : visible seulement quand le bouton
 *   d'ajout de la page a ete DEPASSE vers le haut (jamais avant de l'avoir atteint : Dimi,
 *   14/09/2026), sur telephone comme sur ordinateur (observateur sur
 *   ce bouton, marges = hauteur de l'en-tete collant et de la barre), et retiree des que la
 *   sentinelle de fin de fiche entre dans l'ecran (le pied de page n'est jamais recouvert).
 *   Ecrans servis par data-on ; dessin et transition par .hod-fixbar (hod-base.css §10.8).
 */
import '@theme/view-event-elements';
import { formatMoney } from '@theme/money-formatting';

const parse = (raw, fallback = null) => {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

class HodProduct extends HTMLElement {
  // Initialisation unique (ilot, etat) ; les ecoutes sont reposees a chaque rebranchement
  // (fiche deplacee dans le DOM par l'editeur ou un script tiers).
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    if (this.data) return;
    const island = this.querySelector('script[data-hod-pdp-data]');
    this.data = parse(island ? island.textContent : '', {}) || {};
    this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
    this.variant = this.variants.find((v) => v.id === this.data.selected) || this.variants[0] || null;
    this.offers = new Map();
    this.maps = new WeakMap();
    this.preview = new URLSearchParams(window.location.search).get('hod_preview') || '';
    this.initAccordions();
    if (this.preview) this.applyPreview();
    this.syncOptions();
    // Set pre-coche deja rendu au serveur (lot 5, lignes avec proprietes) : pas de retour a la
    // variante seule avant que <hod-pdp-set> pose son offre, donc aucun saut de prix au chargement.
    // Sans <hod-pdp-set> dans la fiche (bloc retire, desactive, regle sur l'autre fiche), personne
    // ne reposerait l'offre : la variante seule est rendue tout de suite.
    const serverOffer = [...this.querySelectorAll('[data-hod-pdp-atc]')].some((button) =>
      (button.dataset.lines || '').includes('"properties"')
    );
    if (!serverOffer || !this.querySelector('hod-pdp-set')) this.render();
    this.dispatch('hod:pdp:ready', { preview: this.preview });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-hod-option-input]')) return;
    const fieldset = input.closest('[data-hod-option]');
    const index = Number(fieldset ? fieldset.dataset.hodOption : NaN);
    const wanted = this.currentOptions();
    const next =
      this.variants.find((v) => v.options.every((o, i) => o === wanted[i])) ||
      this.variants.find((v) => v.options[index] === wanted[index] && v.available) ||
      this.variants.find((v) => v.options[index] === wanted[index]);
    if (next) this.selectVariant(next.id, { source: 'options' });
  };

  currentOptions() {
    const options = [...((this.variant && this.variant.options) || [])];
    this.querySelectorAll('[data-hod-option]').forEach((fieldset) => {
      const i = Number(fieldset.dataset.hodOption);
      const checked = fieldset.querySelector('[data-hod-option-input]:checked');
      if (checked && Number.isInteger(i)) options[i] = checked.value;
    });
    return options;
  }

  selectVariant(id, { source = 'api', updateUrl = true } = {}) {
    const next = this.variants.find((v) => String(v.id) === String(id));
    if (!next) return null;
    const previous = this.variant;
    this.variant = next;
    this.syncOptions();
    this.applyMaps();
    this.render();
    const gallery = this.querySelector('hod-pdp-gallery');
    if (gallery && typeof gallery.showMedia === 'function') gallery.showMedia(next.media);
    if (updateUrl && this.variants.length > 1) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(next.id));
      window.history.replaceState(window.history.state, '', url.toString());
    }
    this.dispatch('hod:pdp:variant', { variant: next, previous, source });
    return next;
  }

  // Radios de variante calees sur la variante ; valeur sans variante disponible barree.
  syncOptions() {
    const current = this.variant;
    if (!current) return;
    this.querySelectorAll('[data-hod-option]').forEach((fieldset) => {
      const i = Number(fieldset.dataset.hodOption);
      fieldset.querySelectorAll('[data-hod-option-input]').forEach((input) => {
        input.checked = input.value === current.options[i];
        const available = this.variants.some(
          (v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || o === current.options[j])
        );
        const label = input.closest('.hod-pdp-opt__value');
        if (label) label.classList.toggle('is-unavailable', !available);
        // v3, revue, remarque 10 : une valeur epuisee reste cliquable (elle montre son image)
        // mais elle s'annonce comme telle au clavier et au lecteur d'ecran.
        if (available) input.removeAttribute('aria-disabled');
        else input.setAttribute('aria-disabled', 'true');
      });
      // v3, retour 5 : la valeur choisie reste ecrite en clair a droite du libelle, meme quand
      // la case ne montre qu'une image (les valeurs sont parfois des references fournisseur).
      const currentEl = fieldset.querySelector('[data-hod-opt-current]');
      if (currentEl && Number.isInteger(i)) currentEl.textContent = current.options[i] || '';
    });
  }

  // Tables par variante rendues en Liquid : texte, ou HTML (data-hod-map-html) ; valeur vide :
  // l'element (ou son ancetre data-hod-map-hide="selecteur") est masque.
  applyMaps() {
    const id = String(this.variant ? this.variant.id : '');
    this.querySelectorAll('[data-hod-map]').forEach((el) => {
      let map = this.maps.get(el);
      if (!map) {
        map = parse(el.getAttribute('data-hod-map'), {}) || {};
        this.maps.set(el, map);
      }
      const value = has(map, id) && map[id] != null ? String(map[id]) : '';
      if (el.hasAttribute('data-hod-map-html')) el.innerHTML = value;
      else el.textContent = value;
      if (el.hasAttribute('data-hod-map-hide')) {
        const selector = el.getAttribute('data-hod-map-hide');
        const target = selector ? el.closest(selector) : el;
        if (target) target.hidden = !value;
      }
    });
    // Rangee d'huiles : sur la fiche huile, le parfum affiche sort de la rangee.
    const selected = this.data.mode === 'oil' ? id : '';
    this.querySelectorAll('[data-hod-oils]').forEach((list) => {
      const count = Number(list.dataset.count) || 8;
      let shown = 0;
      list.querySelectorAll('[data-hod-oil-item]').forEach((item) => {
        const hide = (selected && item.dataset.variantId === selected) || shown >= count;
        item.hidden = hide;
        if (!hide) shown += 1;
      });
    });
  }

  // Offre : la premiere offre principale posee (set, bundle AOV), sinon la variante seule ; les
  // offres « addon » (upsells coches, plusieurs possibles) ajoutent leurs lignes et leur prix
  // (Dimi, 15/09/2026 : cones ET batons sous le bouton, le client coche l'un, l'autre ou les deux).
  offer() {
    const all = [...this.offers.values()];
    const addons = all.filter((o) => o.addon);
    const main = all.find((o) => !o.addon);
    const v = this.variant;
    const base = main || (v ? { key: 'variant', lines: [{ id: v.id, quantity: 1 }], price: v.price, label: '', available: v.available } : null);
    if (!base || !addons.length) return base;
    const baseCents = base.priceText ? this.parsePrice(base.priceText, base.price) : Number(base.price) || 0;
    const total = addons.reduce((sum, o) => sum + (Number(o.price) || 0), baseCents);
    return {
      ...base,
      label: addons.map((o) => o.label).find(Boolean) || base.label,
      lines: [...(base.lines || []), ...addons.flatMap((o) => o.lines || [])],
      price: total,
      priceText: this.money(total),
      available: base.available !== false && addons.every((o) => o.available !== false),
    };
  }

  // « $3.94 » ou « 3,94 € » -> 394 ; repli sur le prix connu si le texte est illisible.
  parsePrice(text, fallback) {
    const digits = String(text || '').replace(/[^0-9.,]/g, '');
    const normalized = digits.includes(',') && !digits.includes('.') ? digits.replace(',', '.') : digits.replace(/,/g, '');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? Math.round(n * 100) : Number(fallback) || 0;
  }

  setOffer(key, offer) {
    if (offer) this.offers.set(key, { key, ...offer });
    else this.offers.delete(key);
    this.render();
    this.dispatch('hod:pdp:offer', { offer: this.offer() });
  }

  getState() {
    return { variant: this.variant, offer: this.offer(), data: this.data, preview: this.preview };
  }

  money(cents) {
    const money = this.data.money || {};
    try {
      return formatMoney(Number(cents) || 0, money.format || '${{amount}}', money.currency || 'USD');
    } catch {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  // Bouton d'ajout et barre fixe : lignes, libelle « Add to cart · [price] », etat epuise.
  render() {
    const offer = this.offer();
    if (!offer) return;
    const available = offer.available !== false;
    const priceText = offer.priceText || this.money(offer.price);
    const lines = JSON.stringify(offer.lines || []);
    this.querySelectorAll('[data-hod-pdp-atc]').forEach((button) => {
      button.dataset.lines = lines;
      const text = button.querySelector('[data-hod-pdp-atc-text]');
      if (text) {
        text.textContent = available
          ? (button.dataset.label || '').replace('[price]', priceText)
          : button.dataset.labelSoldout || 'Sold out';
      }
      button.disabled = !available;
    });
    const label = offer.label || this.stickyLabel();
    this.querySelectorAll('[data-hod-pdp-offer-label]').forEach((el) => {
      el.textContent = label;
      el.hidden = !label;
    });
    this.querySelectorAll('[data-hod-pdp-offer-price]').forEach((el) => {
      el.textContent = priceText;
    });
  }

  // Libelle de la barre hors set : « Diffuser only », ou « [scent] · 10 ml » sur la fiche huile,
  // recalcule avec le parfum de la variante ; sans parfum, le jeton et son separateur partent.
  stickyLabel() {
    const template = this.dataset.stickyLabel || '';
    if (!template.includes('[scent]')) return template;
    const scent = (this.variant && this.variant.scent) || '';
    const text = scent
      ? template.replace(/\[scent\]/g, scent)
      : template.replace(/\s*\[scent\]\s*[·,]?\s*/g, ' ').replace(/^\s*[·,]\s*|\s*[·,]\s*$/g, '');
    return text.replace(/\s+/g, ' ').trim();
  }

  // « Un seul volet ouvert a la fois » (reglage du bloc Accordeons) : name commun.
  initAccordions() {
    this.querySelectorAll('[data-hod-acc-exclusive]').forEach((group, n) => {
      group.querySelectorAll(':scope > details').forEach((details) => details.setAttribute('name', `hod-pdp-acc-${n}`));
    });
  }

  applyPreview() {
    this.dataset.hodPreview = this.preview;
    this.querySelectorAll('[data-hod-preview-reveal]').forEach((el) => {
      if (el.dataset.hodPreviewReveal.split(/\s+/).includes(this.preview)) el.hidden = false;
    });
  }

  dispatch(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: { ...detail, product: this.data.product, mode: this.data.mode, root: this } })
    );
  }
}

class HodPdpGallery extends HTMLElement {
  connectedCallback() {
    if (!this.bound) {
      this.bound = true;
      this.slides = [...this.querySelectorAll('[data-hod-gal-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-hod-gal-thumb]')];
      this.track = this.querySelector('[data-hod-gal-track]');
      this.thumbBox = this.querySelector('[data-hod-gal-thumbs]');
      this.counters = [...this.querySelectorAll('[data-hod-gal-current]')];
      this.captions = [...this.querySelectorAll('[data-hod-gal-caption]')];
      this.status = this.querySelector('[data-hod-gal-status]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.index = Number(this.dataset.start) || 0;
    }
    this.addEventListener('click', this.onClick);
    if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
    // Le rail mobile est deja cale au rendu (script en ligne du snippet, avant le premier
    // affichage) ; ici seulement la fenetre de vignettes et un rattrapage si besoin.
    if (this.index > 0) {
      requestAnimationFrame(() => {
        if (this.track && this.mobile.matches && Math.round(this.track.scrollLeft / this.slideWidth()) !== this.index) {
          this.scrollTrack(this.index, 'instant');
        }
        this.followThumb(this.index, 'instant');
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const thumb = target.closest('[data-hod-gal-thumb]');
    if (thumb) this.go(Number(thumb.dataset.index));
    else if (target.closest('[data-hod-gal-prev]')) this.go(this.index - 1);
    else if (target.closest('[data-hod-gal-next]')) this.go(this.index + 1);
  };

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.mobile.matches || !this.track) return;
      const i = Math.round(this.track.scrollLeft / this.slideWidth());
      if (i !== this.index && i >= 0 && i < this.slides.length) this.setIndex(i);
    });
  };

  // Largeur d'un pas du rail : celle d'une diapo, pas celle du defileur (v3, retour 1). En mode
  // « peek » la diapo est plus etroite que l'ecran d'un cran, et le rang se lirait faux.
  slideWidth() {
    const slide = this.slides && this.slides[0];
    const w = slide ? slide.getBoundingClientRect().width : 0;
    return w || (this.track && this.track.clientWidth) || 1;
  }

  go(i) {
    const n = this.slides.length;
    if (!n || !Number.isFinite(i)) return;
    const next = ((i % n) + n) % n;
    this.setIndex(next);
    this.scrollTrack(next, 'smooth');
  }

  showMedia(mediaId) {
    if (mediaId == null) return;
    const i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (i >= 0) this.go(i);
  }

  setIndex(i) {
    this.index = i;
    this.slides.forEach((slide, k) => slide.classList.toggle('is-active', k === i));
    this.thumbs.forEach((thumb, k) => thumb.setAttribute('aria-current', k === i ? 'true' : 'false'));
    const n = String(i + 1).padStart(2, '0');
    this.counters.forEach((el) => {
      el.textContent = n;
    });
    const slide = this.slides[i];
    const img = slide ? slide.querySelector('img') : null;
    this.captions.forEach((el) => {
      const text = img ? img.getAttribute('alt') || '' : '';
      el.textContent = text;
      el.hidden = !text;
    });
    // La barre d'achat fixe suit le media affiche (vignette 48 x 48).
    if (img) {
      this.dispatchEvent(
        new CustomEvent('hod:pdp:media', { bubbles: true, detail: { index: i, src: img.currentSrc || img.src || img.dataset.src || '' } })
      );
    }
    if (this.status) {
      this.status.textContent = (this.status.dataset.template || '')
        .replace('[n]', String(i + 1))
        .replace('[total]', String(this.slides.length));
    }
    this.followThumb(i);
  }

  // La fenetre de vignettes suit la vignette active (04 S3 : scrollTop borne).
  // Rangee horizontale (moins de 900 px), review v3 du 13/09, point 1 : la rangee ne bouge que si
  // la vignette active n'est pas entiere, et elle se pose alors sur le bord d'une vignette (celle
  // d'avant, pour garder le contexte), donc sur le bord gauche de l'image : offsetLeft est lu
  // dans la rangee elle-meme (position: relative, aucun retrait), 0 pour la premiere. En fin de
  // course la derniere vignette finit sur le bord droit de l'image.
  followThumb(i, behavior = 'smooth') {
    const box = this.thumbBox;
    const thumb = this.thumbs[i];
    if (!box || !thumb) return;
    if (box.scrollHeight > box.clientHeight + 1) {
      box.scrollTop = Math.min(Math.max(thumb.offsetTop, 0), box.scrollHeight - box.clientHeight);
    } else if (box.scrollWidth > box.clientWidth + 1) {
      const view = box.scrollLeft;
      const left = thumb.offsetLeft;
      const right = left + thumb.offsetWidth;
      if (left >= view - 1 && right <= view + box.clientWidth + 1) return;
      const anchor = this.thumbs[i - 1] || thumb;
      const target = Math.min(Math.max(anchor.offsetLeft, 0), box.scrollWidth - box.clientWidth);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      box.scrollTo({ left: target, behavior: reduce ? 'instant' : behavior });
    }
  }

  scrollTrack(i, behavior) {
    const track = this.track;
    if (!track || !this.mobile.matches) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: i * this.slideWidth(), behavior: reduce ? 'instant' : behavior });
  }
}

class HodPdpSticky extends HTMLElement {
  // SPEC V2 §3.2 : la barre n'est visible que lorsque le bouton d'ajout de la page n'est pas a
  // l'ecran, sur telephone comme sur ordinateur, et elle se retire avant le pied de page.
  // Observateur 1 : le bouton de la page, avec une marge qui retire la hauteur de l'en-tete
  //   collant et celle de la barre (un bouton cache dessous n'est pas « a l'ecran »).
  // Observateur 2 : la sentinelle [data-hod-bar-end] posee avant la fin de la fiche.
  // A chaque branchement : observateurs et ecoutes recrees (ils sont retires au debranchement).
  connectedCallback() {
    this.screens = (this.dataset.on || 'mobile tablet desktop').trim().split(/\s+/);
    if (!this.mqMobile) {
      this.mqMobile = window.matchMedia('(max-width: 749px)');
      this.mqTablet = window.matchMedia('(min-width: 750px) and (max-width: 899px)');
    }
    const root = this.closest('hod-product') || document;
    this.root = root;
    this.atc = root.querySelector('[data-hod-pdp-atc-main]');
    this.end = root.querySelector('[data-hod-bar-end]');
    this.away = false;
    this.atEnd = false;
    this.arm();
    this.mqMobile.addEventListener('change', this.onBreak);
    this.mqTablet.addEventListener('change', this.onBreak);
    window.addEventListener('resize', this.onResize, { passive: true });
    // Filet de securite du defilement : un saut qui franchit d'un coup le bouton ou la
    // sentinelle ne change aucun taux d'intersection, donc aucun observateur ne rappelle et
    // la barre resterait dans son dernier etat (par-dessus le pied de page). Ecoute passive,
    // en capture pour attraper aussi le defilement de .page-wrapper, et une seule mise a jour
    // par image grace a requestAnimationFrame.
    window.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    root.addEventListener('hod:pdp:media', this.onMedia);
    this.update();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.ioEnd) this.ioEnd.disconnect();
    if (this.mqMobile) this.mqMobile.removeEventListener('change', this.onBreak);
    if (this.mqTablet) this.mqTablet.removeEventListener('change', this.onBreak);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll, { capture: true });
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.root) this.root.removeEventListener('hod:pdp:media', this.onMedia);
    if (this.timer) clearTimeout(this.timer);
    this._barVar = null;
    document.body.style.removeProperty('--hod-fixed-bar-h');
  }

  // Hauteurs du moment : en-tete collant (publie sur <body> par hod-ui.js) et barre elle-meme.
  metrics() {
    const read = (name, fallback) => {
      const raw = getComputedStyle(document.body).getPropertyValue(name).trim();
      const value = parseFloat(raw);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };
    const header = read('--hod-header-h', this.mqMobile.matches ? 57 : 70);
    const bar = this.offsetHeight || (this.mqMobile.matches ? 84 : 72);
    // Hauteur retenue pour la mise a jour du defilement : elle ne change qu'au branchement,
    // au changement de palier et au redimensionnement, jamais a chaque image (revue v2).
    this.barH = Math.round(bar);
    this.headerH = Math.round(header);
    return { header: this.headerH, bar: this.barH };
  }

  arm() {
    if (this.io) this.io.disconnect();
    if (this.ioEnd) this.ioEnd.disconnect();
    this.io = null;
    this.ioEnd = null;
    if (!('IntersectionObserver' in window)) return;
    const { header, bar } = this.metrics();
    if (this.atc) {
      this.io = new IntersectionObserver(
        ([entry]) => {
          // Seulement une fois le bouton depasse vers le haut : en haut de fiche, le bouton est
          // encore sous l'ecran et la barre ne doit pas etre la (Dimi, 14/09/2026).
          this.away = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.update();
        },
        { rootMargin: `-${header}px 0px -${bar}px 0px`, threshold: 0 }
      );
      this.io.observe(this.atc);
    }
    if (this.end) {
      this.ioEnd = new IntersectionObserver(([entry]) => {
        // Une fois la sentinelle atteinte, la barre reste repliee : sans le test sur
        // boundingClientRect, elle revient des que la sentinelle sort par le haut et
        // recouvre le pied de page (constate au rendu, integration v2).
        this.atEnd = entry.isIntersecting || entry.boundingClientRect.top < 0;
        this.update();
      });
      this.ioEnd.observe(this.end);
    }
  }

  onBreak = () => {
    this.arm();
    this.update();
  };

  onScroll = (event) => {
    // Ecoute en capture : elle voit aussi le defilement du rail de galerie et de tout autre
    // conteneur qui defile. Seuls le document et .page-wrapper deplacent la fiche (revue v2).
    const target = event && event.target;
    if (target && target.nodeType === 1 && target !== document.documentElement
      && target !== document.body && !target.classList.contains('page-wrapper')) return;
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.update();
    });
  };

  // Les marges d'un observateur sont figees a sa creation : on le refait apres un
  // redimensionnement (barre plus haute, en-tete replie), jamais a chaque pixel.
  onResize = () => {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = 0;
      this.arm();
      this.update();
    }, 150);
  };

  // Vignette de la barre : le media affiche par la galerie (evenement de <hod-pdp-gallery>).
  onMedia = (event) => {
    const box = this.querySelector('[data-hod-sticky-thumb]');
    const img = box && box.querySelector('img');
    const detail = event.detail || {};
    if (!img || !detail.src) return;
    img.removeAttribute('srcset');
    img.src = detail.src;
  };

  allowed() {
    if (this.mqMobile.matches) return this.screens.includes('mobile');
    if (this.mqTablet.matches) return this.screens.includes('tablet');
    return this.screens.includes('desktop');
  }

  update = () => {
    // Bouton absent du flux (regle « Masquer sous 750 px ») : il n'est jamais a l'ecran.
    const inFlow = Boolean(this.atc) && this.atc.getClientRects().length > 0;
    // Fin de fiche lue sur la geometrie, pas seulement sur l'observateur : un saut qui
    // franchit la sentinelle d'un coup (ancre, touche Fin) ne change pas le taux
    // d'intersection, donc l'observateur ne rappelle pas et la barre resterait sur le
    // pied de page. Un rect.top sous la hauteur d'ecran couvre les deux cas : sentinelle
    // a l'ecran, et sentinelle deja depassee.
    // Le seuil est le bord HAUT de la barre, pas le bas de l'ecran : la barre tenait jusqu'a
    // l'entree de la sentinelle dans l'ecran et disparaissait donc pendant toute la FAQ, alors
    // que le pied de page etait encore a une hauteur d'ecran (revue v2). Avec le nouveau seuil
    // elle se replie quand le pied de page arrive a son bord haut : il n'est jamais recouvert
    // et la FAQ garde son bouton.
    const barH = this.barH || this.offsetHeight || 72;
    const gap = parseFloat(this.dataset.endGap || '0') || 0;
    const limit = Math.max(0, window.innerHeight - barH - gap);
    const end = this.end && this.end.getClientRects().length > 0
      ? this.end.getBoundingClientRect().top < limit
      : this.atEnd;
    // Bouton depasse lu sur la geometrie aussi : un saut (ancre, touche Fin) ne rappelle pas
    // toujours l'observateur. Depasse = son bas est remonte sous l'en-tete collant.
    const passed = inFlow
      ? this.atc.getBoundingClientRect().bottom <= (this.headerH || 0)
      : true;
    const on = this.allowed() && passed && !end;
    this.classList.toggle('is-on', on);
    this.classList.toggle('is-off', !on);
    // Hauteur exposee pour la reserve de page et une bulle de chat future (C05). Une propriete
    // personnalisee posee sur <body> invalide le style de tout le document : on n'ecrit que
    // lorsque la valeur change, pas a chaque image de defilement (revue v2).
    const next = on ? `${barH}px` : '0px';
    if (next !== this._barVar) {
      this._barVar = next;
      document.body.style.setProperty('--hod-fixed-bar-h', next);
    }
  };
}

if (!customElements.get('hod-product')) customElements.define('hod-product', HodProduct);
if (!customElements.get('hod-pdp-gallery')) customElements.define('hod-pdp-gallery', HodPdpGallery);
if (!customElements.get('hod-pdp-sticky')) customElements.define('hod-pdp-sticky', HodPdpSticky);

/* -------------------------------------------------------------------------- */
/* Pont AOV.ai (Avada Volume, app de bundle installee par Dimi le 15/09/2026)   */
/* Lecture seule du widget : l'offre cochee (Buy 2, Buy 3...) donne le prix du  */
/* bouton d'ajout et de la barre fixe. Les lignes restent celles de la variante */
/* (quantite 1) : c'est l'app qui intercepte l'ajout et pose la quantite, la    */
/* remise et le cadeau (verifie au panier : Buy 2 = 2 pieces remisees + cadeau).*/
/* Buy 1 ou widget absent : aucune offre, le prix de la variante reste.         */
/* -------------------------------------------------------------------------- */
(() => {
  const SEL_WIDGET = '.Avada-Bundle-Offer__Volume';
  const SEL_ITEM = '.Avada-Volume__Item';
  const SEL_ON = '.Avada-Volume__Item--Selected';
  const SEL_PRICE = '.AOV-Offer__DiscountPrice';

  const bridge = (root) => {
    if (root.__hodAov) return;
    root.__hodAov = true;
    let last = null;
    let raf = 0;

    const read = () => {
      raf = 0;
      if (typeof root.setOffer !== 'function' || !root.offers) return;
      const widget = root.querySelector(SEL_WIDGET);
      const selected = widget ? widget.querySelector(SEL_ON) : null;
      const items = widget ? [...widget.querySelectorAll(SEL_ITEM)] : [];
      const priceEl = selected ? selected.querySelector(SEL_PRICE) : null;
      const text = priceEl ? priceEl.textContent.replace(/\s+/g, ' ').trim() : '';
      const variant = root.variant;
      const active = Boolean(selected && variant && text && items.indexOf(selected) > 0);
      const key = active ? `${variant.id}|${text}` : 'none';
      if (key === last) return;
      last = key;
      if (!active) {
        if (root.offers.has('aov')) root.setOffer('aov', null);
        return;
      }
      root.setOffer('aov', {
        lines: [{ id: variant.id, quantity: 1 }],
        price: variant.price,
        priceText: text,
        label: '',
        available: variant.available !== false,
      });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };

    // Le widget arrive apres coup (script de l'app charge en asynchrone) : on observe la colonne
    // d'achat, puis le widget lui-meme (classes de selection et prix recalcules par l'app).
    const buy = root.querySelector('.hod-pdp__buy') || root;
    const watch = new MutationObserver((records) => {
      for (const r of records) {
        const node = r.target instanceof Element ? r.target : null;
        if (node && node.closest(SEL_WIDGET)) {
          schedule();
          return;
        }
        if (r.type === 'childList' && [...r.addedNodes].some((n) => n instanceof Element && (n.matches(SEL_WIDGET) || n.querySelector(SEL_WIDGET) || n.closest?.(SEL_WIDGET)))) {
          schedule();
          return;
        }
      }
    });
    watch.observe(buy, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'], characterData: true });
    root.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest(SEL_WIDGET)) setTimeout(schedule, 60);
    });
    root.addEventListener('hod:pdp:variant', () => {
      last = null;
      setTimeout(schedule, 300);
    });
    schedule();
  };

  const start = () => document.querySelectorAll('hod-product').forEach(bridge);
  customElements.whenDefined('hod-product').then(() => {
    start();
    setTimeout(start, 1500);
  });
})();
